import React, { useEffect, useState } from "react";
import axiosInstance from "../axios/axios";
import { useParams } from "react-router-dom";
import SubmissionSuccess from "./SubmissionSuccess";
import SubmissionError from "./SubmissionError";
import Loader from "./Loader";
import SuccessMessage from "./SuccessMessage";
import ErrorMessage from "./ErrorMessage";

const ApplyJob = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [uploadedResume, setUploadedResume] = useState({});
  const [uploadedInternshipLetter, setUploadedInternshipLetter] = useState({});
  const [coverLetter, setCoverLetter] = useState("");
  const [displaySuccess, setDisplaySuccess] = useState(false);

  // Additional questions state
  const [jobQuestions, setJobQuestions] = useState({});
  const [answers, setAnswers] = useState({
    a1: "",
    a2: "",
    a3: "",
    a4: "",
    a5: "",
  });

  const { org_id } = useParams();
  const user_id = localStorage.getItem("user_id");
  const job_id = localStorage.getItem("job_id");

  // Fetch user and job details
  useEffect(() => {
    const fetchUserAndJob = async () => {
      try {
        const response = await axiosInstance.get("all_users");
        const foundUser = response.data.users.find(
          (u) => u.user_id === parseInt(user_id)
        );
        if (foundUser) {
          setUser(foundUser);
          const { full_name, university, course_of_study } = foundUser.student;
          if (!full_name || !university || !course_of_study) {
            setError("Required student information is missing.");
            setLoading(false);
            return;
          }
          setError(null);
        } else {
          setError("User not found");
        }

        // Fetch job details for additional questions
        const jobRes = await axiosInstance.get(`/view/jobs/${job_id}`);
        setJobQuestions({
          q1: jobRes.data.job.q1,
          q2: jobRes.data.job.q2,
          q3: jobRes.data.job.q3,
          q4: jobRes.data.job.q4,
          q5: jobRes.data.job.q5,
        });
      } catch (error) {
        setError("Failed to fetch user or job data");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserAndJob();
  }, [user_id, job_id]);

  const handleResumeUpload = (e) => {
    const file = e.target.files[0];
    if (file) setUploadedResume(file);
  };
  const handleInternshipLetterUpload = (e) => {
    const file = e.target.files[0];
    if (file) setUploadedInternshipLetter(file);
  };

  // Handle additional question answers
  const handleAnswerChange = (e, key) => {
    setAnswers((prev) => ({
      ...prev,
      [key]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;

    const formData = new FormData();
    formData.append("org_id", org_id);
    formData.append("job_id", job_id);
    formData.append("cover_letter", coverLetter);
    formData.append("user_id", user_id);
    formData.append("resume", uploadedResume);
    formData.append("internship_letter", uploadedInternshipLetter);

    // Append additional answers if any question is present
    Object.keys(answers).forEach((key, idx) => {
      if (jobQuestions[`q${idx + 1}`]) {
        formData.append(key, answers[key]);
      }
    });

    try {
      setLoading(true);
      const response = await axiosInstance.post("apply-job", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSuccess(true);
      setDisplaySuccess(true);
    } catch (error) {
      setLoading(false);
      setError(
        error.response?.data?.error || "Failed to submit the application."
      );
    } finally {
      setLoading(false);
    }
  };

  // Check if any additional question is present
  const additionalQuestions = [
    { key: "a1", question: jobQuestions.q1 },
    { key: "a2", question: jobQuestions.q2 },
    { key: "a3", question: jobQuestions.q3 },
    { key: "a4", question: jobQuestions.q4 },
    { key: "a5", question: jobQuestions.q5 },
  ];
  const showAdditionalQuestions = additionalQuestions.some(
    (item) => item.question && item.question !== null && item.question !== ""
  );

  const isFormValid =
    uploadedResume?.name &&
    uploadedInternshipLetter?.name &&
    coverLetter.trim() !== "" &&
    // If additional questions are present, allow them to be optional
    true;

  if (loading) return <Loader />;

  return (
    <div className="flex flex-col h-screen relative w-full">
      {error && <ErrorMessage message={error} />}
      <div className="flex flex-col justify-between items-center w-full top-bar">
        <div className="flex justify-between py-3 px-2 items-center w-full">
          <span>Application</span>
          <span className="!text-sm !font-normal">Complete Application</span>
        </div>
      </div>
      <section className="md:mt-[40px] flex p-[24px] flex-col gap-6">
        <div className="flex w-full justify-between custom-section xsm:flex-col">
          <div className="flex flex-col gap-4">
            <span className="label-name w-full">Full Name</span>
            <span className="content-container">
              {user?.student.full_name || "N/A"}
            </span>
          </div>
          <div className="flex flex-col gap-4">
            <span className="label-name">Email Address</span>
            <span className="content-container">{user?.email || "N/A"}</span>
          </div>
        </div>
        <div className="flex w-full justify-between custom-section xsm:flex-col">
          <div className="flex flex-col gap-4">
            <span className="label-name w-full">University</span>
            <span className="content-container">
              {user?.student.university || "N/A"}
            </span>
          </div>
          <div className="flex flex-col gap-4">
            <span className="label-name w-full">Course Of Study</span>
            <span className="content-container">
              {user?.student.course_of_study || "N/A"}
            </span>
          </div>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="flex w-full justify-between custom-section xsm:flex-col">
            <div className="flex flex-col gap-4">
              <span className="label-name">Resume</span>
              <label htmlFor="resume" className="custom-file-input">
                <span className="file-input-label">
                  {uploadedResume ? uploadedResume.name : "Upload Resume"}
                </span>
                <input
                  required
                  type="file"
                  name="resume"
                  id="resume"
                  className="hidden-input"
                  onChange={handleResumeUpload}
                />
              </label>
            </div>
            <div className="flex flex-col gap-4">
              <span className="label-name">Internship Letter</span>
              <label htmlFor="internship-letter" className="custom-file-input">
                <span className="file-input-label">
                  {uploadedInternshipLetter
                    ? uploadedInternshipLetter.name
                    : "Upload Internship Letter"}
                </span>
                <input
                  required
                  type="file"
                  name="internshipLetter"
                  id="internship-letter"
                  className="hidden-input"
                  onChange={handleInternshipLetterUpload}
                />
              </label>
            </div>
          </div>
          <div className="flex gap-3 flex-col my-3">
            <label htmlFor="coverLetter">Cover Letter</label>
            <textarea
              required
              name="coverLetter"
              className="custom-form"
              id="coverLetter"
              value={coverLetter}
              onChange={(e) => setCoverLetter(e.target.value)}
            ></textarea>
          </div>

          {/* Additional Questions Section */}
          {showAdditionalQuestions && (
            <div className="flex flex-col gap-4 my-4">
              <h3 className="font-semibold text-base mb-2">
                Additional Questions from Organization
              </h3>
              {additionalQuestions.map(
                (item, idx) =>
                  item.question && (
                    <div key={item.key} className="flex flex-col gap-2">
                      <label className="label-name">
                        {item.question}
                        <span className="text-xs text-gray-400 ml-2">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        className="custom-form"
                        value={answers[item.key]}
                        onChange={(e) => handleAnswerChange(e, item.key)}
                        placeholder={`Answer for question ${idx + 1}`}
                      />
                    </div>
                  )
              )}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-success my-3 w-full"
            disabled={!isFormValid}
          >
            Apply
          </button>
        </form>
        {success && (
          <SuccessMessage
            message="Your action was successful!"
            onClose={() => setSuccess(false)}
            duration={3000}
          />
        )}
      </section>
      {error && <SubmissionError />}
      {displaySuccess && <SubmissionSuccess />}
    </div>
  );
};

export default ApplyJob;